import type { AxiosResponse } from "axios";
import { AXIOS_POST_TIMEOUT_MS } from "src/config/axiosTimeout";
import { isBypassedOrganization } from "src/config/marketingActionBypass";
import type { ConcurrentAction, ReactivationStrategyEnum } from "src/types/offer/ConcurrentAction";
import { OfferStatus } from "src/types/offerSlot";
import axios from "src/utils/axios";
import { extendMarketingActionWithMainScenario } from "src/utils/formatters";
import type { OfferFormOutput } from "src/views/marketing-actions/marketing-actions-create-view/types/MarketingActionFormOutput";

export const createOffer = async (
    offerFormCreation: OfferFormOutput,
    organizationId?: number
): Promise<AxiosResponse<any>> => {
    try {
        // Do not send organizationId as query param. Server identifies the user.
        return await axios.post(`/offer`, offerFormCreation, { timeout: AXIOS_POST_TIMEOUT_MS });
    } catch (e: any) {
        // Only return fallback when it's a timeout AND org id is in bypass list
        if (e?.code === "ECONNABORTED" && isBypassedOrganization(organizationId)) {
            return { data: { offer: { status: OfferStatus.InCreation } } } as AxiosResponse<any>;
        }
        return;
    }
};

export const cancelOffer = async (
    offerId: number,
    posIds: number[],
    action: string,
    { reactivationStrategy }: { reactivationStrategy?: ReactivationStrategyEnum } = {}
): Promise<{ pass: boolean; overrides?: ConcurrentAction[] }> => {
    const response = await axios.patch<{ pass: boolean; overrides?: ConcurrentAction[] }>(
        `/offer/${offerId}`,
        {
            action,
            pos: posIds,
            reactivationStrategy,
        }
    );

    return {
        ...response.data,
        overrides: response.data.overrides && [
            ...response.data.overrides.map((ove) => {
                return {
                    ...ove,
                    marketingAction: extendMarketingActionWithMainScenario(ove.marketingAction),
                };
            }),
        ],
    };
};

export const cancelOfferSlot = async (offerSlotId: number, scope: string): Promise<void> => {
    await axios.patch(`/offerslot/${offerSlotId}`, {
        action: "cancel",
        scope: scope,
    });
};

export const suspendOfferSlot = async (
    offerSlotId: number,
    scope: string,
    action: string
): Promise<AxiosResponse<any>> => {
    const response = await axios.patch(`/offerslot/${offerSlotId}`, {
        action: action,
        scope: scope,
    });
    return response;
};
